import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  FareClassOptionFilter,
  IFlightNumberFilter,
  ITimeRange,
} from "@b2bportal/core-types";
import {
  getDepartureFiltersOptions,
  getReturnFiltersOptions,
} from "../../../shop/store";
import {
  FlightSearchActions,
  getFlightNumberFilter,
  getOutboundArrivalTimeRange,
  getOutboundDepartureTimeRange,
  getReturnArrivalTimeRange,
  getReturnDepartureTimeRange,
} from "../../store";
import {
  getAirlineFilter,
  getAirportFilter,
  getDestination,
  getFareclassOptionFilter,
  getMaxPriceFilter,
  getOrigin,
  getStopsOption,
  getTripCategory,
} from "../../store/selectors";
import type { FilterDialogComponentProps } from "./FilterDialog";

export const useFilterDialog = ({
  sliceIndex,
  applyAirlineFiltersToFlightNumber = false,
}: FilterDialogComponentProps) => {
  const dispatch = useDispatch();
  const origin = useSelector(getOrigin);
  const destination = useSelector(getDestination);
  const departureFilterOptions = useSelector(getDepartureFiltersOptions);
  const returnFilterOptions = useSelector(getReturnFiltersOptions);
  const tripCategory = useSelector(getTripCategory);

  const filterOptions =
    sliceIndex === 0 ? departureFilterOptions : returnFilterOptions;

  /** State data  */
  const stopsState = useSelector(getStopsOption);
  const priceState = useSelector(getMaxPriceFilter);
  const fareClassesState = useSelector(getFareclassOptionFilter);
  const airlineFilter = useSelector(getAirlineFilter);
  const airportFilter = useSelector(getAirportFilter);
  const outboundDepartureTimeRange = useSelector(getOutboundDepartureTimeRange);
  const outboundArrivalTimeRange = useSelector(getOutboundArrivalTimeRange);
  const returnDepartureTimeRange = useSelector(getReturnDepartureTimeRange);
  const returnArrivalTimeRange = useSelector(getReturnArrivalTimeRange);
  const flightNumberFilter = useSelector(getFlightNumberFilter);

  /** Component data  */
  const [stop, setStop] = useState(stopsState);
  const [price, setPrice] = useState(priceState);
  const [fareClasses, setFareClasses] = useState(fareClassesState);
  const [airline, setAirline] = useState(airlineFilter);
  const [airport, setAirport] = useState(airportFilter);
  const [
    outboundDepartureTimeRangeInternal,
    setOutboundDepartureTimeRangeInternal,
  ] = useState(outboundDepartureTimeRange);
  const [
    outboundArrivalTimeRangeInternal,
    setOutboundArrivalTimeRangeInternal,
  ] = useState(outboundArrivalTimeRange);
  const [
    returnDepartureTimeRangeInternal,
    setReturnDepartureTimeRangeInternal,
  ] = useState(returnDepartureTimeRange);
  const [returnArrivalTimeRangeInternal, setReturnArrivalTimeRangeInternal] =
    useState(returnArrivalTimeRange);
  const [flightNumber, setFlightNumber] = useState(flightNumberFilter);

  const flightNumbersByAirline = useMemo(() => {
    if (
      applyAirlineFiltersToFlightNumber &&
      (airline?.length ?? 0) > 0 &&
      filterOptions.flightNumbersByAirline != null
    ) {
      // filter out other airlines
      return Object.fromEntries(
        Object.entries(filterOptions.flightNumbersByAirline).filter(([key]) =>
          airline.includes(key)
        )
      );
    } else {
      return filterOptions.flightNumbersByAirline;
    }
  }, [
    airline,
    applyAirlineFiltersToFlightNumber,
    filterOptions.flightNumbersByAirline,
  ]);

  const handleStopsChange = (newStop) => {
    setStop(newStop);
  };

  const handlePriceChange = ({ maxPrice }) => {
    setPrice(maxPrice);
  };

  const handleFareClassesChange = (newFareClasses: FareClassOptionFilter) => {
    setFareClasses(newFareClasses);
  };

  const handleAirlineChange = (newAirlines: string[]) => {
    setAirline(newAirlines);
  };

  const handleFlightNumberChange = (flightNumbers: IFlightNumberFilter[]) => {
    setFlightNumber(flightNumbers);
  };

  const handleOutboundTimeRangeChange = (
    outboundDepartureTimeRange: ITimeRange,
    outboundArrivalTimeRange: ITimeRange
  ) => {
    setOutboundDepartureTimeRangeInternal(outboundDepartureTimeRange);
    setOutboundArrivalTimeRangeInternal(outboundArrivalTimeRange);
  };
  const handleReturnTimeRangeChange = (
    returnDepartureTimeRange: ITimeRange,
    returnArrivalTimeRange: ITimeRange
  ) => {
    setReturnDepartureTimeRangeInternal(returnDepartureTimeRange);
    setReturnArrivalTimeRangeInternal(returnArrivalTimeRange);
  };

  const updateValuesToMatchState = () => {
    setStop(stopsState);
    setAirline(airlineFilter);
    setAirport(airportFilter);
    setPrice(priceState);
    setFlightNumber(flightNumberFilter);
    setFareClasses(fareClassesState);
    setOutboundDepartureTimeRangeInternal(outboundDepartureTimeRange);
    setOutboundArrivalTimeRangeInternal(outboundArrivalTimeRange);
    setReturnDepartureTimeRangeInternal(returnDepartureTimeRange);
    setReturnArrivalTimeRangeInternal(returnArrivalTimeRange);
  };

  const resetAllFilters = () => {
    dispatch(FlightSearchActions.resetFilters());
  };

  const applyFilter = () => {
    dispatch(
      FlightSearchActions.setAllFilters({
        stopsOption: stop,
        maxPriceFilter: price,
        fareclassOptionFilter: fareClasses,
        airlineFilter: airline,
        airportFilter: airport,
        flightNumberFilter: flightNumber,
        outboundDepartureTimeRange: outboundDepartureTimeRangeInternal,
        outboundArrivalTimeRange: outboundArrivalTimeRangeInternal,
        returnDepartureTimeRange: returnDepartureTimeRangeInternal,
        returnArrivalTimeRange: returnArrivalTimeRangeInternal,
      })
    );
  };

  return {
    context: {
      fareClasses,
      stop,
      filterOptions,
      airline,
      origin,
      destination,
      outboundDepartureTimeRange,
      outboundArrivalTimeRange,
      returnDepartureTimeRange,
      returnArrivalTimeRange,
      tripCategory,
      priceState,
      flightNumbersByAirline,
      flightNumber,
    },
    handlers: {
      handleFareClassesChange,
      handleAirlineChange,
      handleOutboundTimeRangeChange,
      handleReturnTimeRangeChange,
      handlePriceChange,
      handleFlightNumberChange,
      handleStopsChange,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
    },
  };
};
